/**
 * The microphone, the AudioContext graph, and nothing else.
 *
 * THE CONSTRAINTS ARE THE MOST IMPORTANT LINES IN THIS FILE.
 * `config.audio.constraints` sets noiseSuppression, autoGainControl and
 * echoCancellation all false. Chrome's noise suppressor classifies trills and
 * raspberries as noise and *removes them*: you detect nothing, and it presents
 * as broken DSP rather than as a bad constraint. Do not accept the defaults, and
 * do not let a refactor drop them.
 *
 * Asking is not the same as getting, which is why `stats.settings` reports what
 * the track actually applied rather than what was requested. A browser is free
 * to ignore a constraint, and two `getUserMedia` calls live at once — the
 * camera's in Phase 1 and this one — is exactly the situation where that
 * happens. Reading the applied values first is a minute; debugging a dead
 * raspberry detector is an afternoon.
 *
 * The graph:
 *
 *     MediaStreamSource ─┬─> AnalyserNode          (rAF: spectrum, ZCR, f0)
 *                        └─> AudioWorkletNode ──> Gain(0) ──> destination
 *                                                 (envelope, ~375 Hz)
 *
 * The worklet branch runs to the destination through a gain of zero. Not for
 * monitoring — for scheduling: a node outside the path to a destination is not
 * guaranteed to be pulled, and a worklet that stops being called simply stops
 * producing an envelope, silently. The zero gain is what keeps that connection
 * from putting the microphone into the speakers, which with echo cancellation
 * deliberately off is a feedback loop.
 */

import { config } from '../core/config.js';
import { AudioAnalyser, EnvelopeTracker, toDb } from './analyser.js';
import { AudioClock } from './clock.js';
import { EnvelopeAnalyser } from './envelope.js';
import { NoiseFloor } from './noisefloor.js';
import workletUrl from './rms-worklet.js?url';

/** Constraints the request depends on, and their meaning if ignored. */
const CRITICAL = {
  noiseSuppression: 'trills and raspberries are being removed before you see them',
  autoGainControl: 'the dynamics attack is measured from are being flattened',
  echoCancellation: 'the quiet parts of a trill are being gated out',
};

export class AudioCapture {
  /**
   * @param {object} o
   * @param {import('../core/ringbuffer.js').RingBuffer} o.buffer  envelope samples land here
   * @param {(state: string, detail?: string) => void} [o.onState]
   * @param {(sample: {t: number, rms: number}) => void} [o.onEnvelope]
   *   every envelope sample as it is produced, for the recorder — which hangs
   *   off this rather than polling the buffer, so a wrap loses nothing.
   */
  constructor({ buffer, onState, onEnvelope, onGesture }) {
    this.buffer = buffer;
    this.onState = onState ?? (() => {});
    this.onEnvelope = onEnvelope ?? (() => {});

    /** 'idle' | 'starting' | 'running' | 'error' */
    this.state = 'idle';
    this.stats = {
      sampleRate: 0,
      blockSize: 0,
      envelopeRate: 0,
      batches: 0,
      /** What the track actually applied. @type {?MediaTrackSettings} */
      settings: null,
      /** Requested constraints that did not take. @type {string[]} */
      ignored: [],
      /** Constraints this browser does not report either way. @type {string[]} */
      unreported: [],
      /** Audio-clock to performance.now() offset, ms. @type {?number} */
      clockOffset: null,
      /** The graph has stopped being fed. See _stalled(). */
      stalled: false,
      /** AudioContext state, which is not always 'running'. */
      contextState: null,
      /** Times the graph was found dead and restarted. */
      recoveries: 0,
      /** What the room is doing, and what a sound has to clear. */
      noiseFloorDb: null,
      gateDb: null,
    };

    /** @type {?AudioAnalyser} */
    this.analyser = null;
    /** What "quiet" is here, right now. Every level threshold reads this. */
    this.noiseFloor = new NoiseFloor();
    this.tracker = new EnvelopeTracker({ noiseFloor: this.noiseFloor });
    /** Phase 5: FFT of the envelope, for trills. */
    this.envelope = new EnvelopeAnalyser({ onGesture: onGesture ?? (() => {}) });

    this._ctx = null;
    this._stream = null;
    this._node = null;
    this._analyserNode = null;
    this._clock = null;
    /** performance.now() of the last envelope batch. The liveness signal. */
    this._lastBatchAt = 0;
    this._recovering = false;
  }

  get running() {
    return this.state === 'running';
  }

  /**
   * The microphone is open, whether or not data is currently flowing.
   *
   * `running` and `active` differ exactly during a stall, and the distinction
   * is the point: the button must still say "stop microphone" and the recorder
   * must stay armed, while nothing may treat the analyser's output as data.
   */
  get active() {
    return this.state === 'running' || this.state === 'stalled';
  }

  _set(state, detail) {
    this.state = state;
    this.onState(state, detail);
  }

  /** Open the microphone and start the graph. Must be called from a gesture. */
  async start() {
    if (this.state === 'running' || this.state === 'starting') return;
    this._set('starting', 'requesting microphone');

    try {
      this._stream = await navigator.mediaDevices.getUserMedia({
        audio: { ...config.audio.constraints },
        video: false,
      });

      const track = this._checkApplied();

      this._set('starting', 'building the graph');
      // 'interactive' asks for the smallest buffer the device will give. Every
      // millisecond of it is latency between a sound and its glyph.
      this._ctx = new AudioContext({ latencyHint: 'interactive' });
      // Created suspended in some browsers even from a gesture.
      if (this._ctx.state === 'suspended') await this._ctx.resume();

      await this._ctx.audioWorklet.addModule(workletUrl);

      const source = this._ctx.createMediaStreamSource(this._stream);

      this._analyserNode = this._ctx.createAnalyser();
      this._analyserNode.fftSize = config.audio.fftSize;
      // Ours is a transient detector. The built-in smoothing is an exponential
      // average over frames, which is precisely a low-pass filter on the thing
      // being measured.
      this._analyserNode.smoothingTimeConstant = config.audio.smoothingTimeConstant;
      source.connect(this._analyserNode);

      this._node = new AudioWorkletNode(this._ctx, 'rms-processor', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        processorOptions: { batch: config.audio.WORKLET_BATCH },
      });
      this._node.port.onmessage = (e) => this._onBatch(e.data);
      source.connect(this._node);

      const silent = this._ctx.createGain();
      silent.gain.value = 0;
      this._node.connect(silent).connect(this._ctx.destination);

      this._clock = new AudioClock({
        sampleRate: this._ctx.sampleRate,
        window: config.audio.CLOCK_WINDOW,
      });

      this.stats.sampleRate = this._ctx.sampleRate;
      this.stats.batches = 0;
      this.stats.stalled = false;
      this.stats.contextState = this._ctx.state;
      this._lastBatchAt = performance.now();
      this.noiseFloor.reset();
      this.envelope.reset();
      this.analyser = new AudioAnalyser(this._analyserNode, this.tracker, this.noiseFloor);

      this._watch(track);
      this._set('running', `${Math.round(this._ctx.sampleRate / 1000)} kHz`);
    } catch (err) {
      this.stop();
      this._set('error', err?.message ?? String(err));
      throw err;
    }
  }

  /**
   * Compare what was asked for against what the track applied.
   *
   * `getSettings()` may not mention a constraint at all, which is not the same
   * as it having been ignored — it means this browser does not say. Those are
   * reported separately rather than folded into either verdict.
   */
  _checkApplied() {
    const track = this._stream?.getAudioTracks?.()[0];
    const settings = track?.getSettings?.() ?? {};
    this.stats.settings = settings;
    this.stats.ignored = [];
    this.stats.unreported = [];

    for (const [key, consequence] of Object.entries(CRITICAL)) {
      const applied = settings[key];
      if (applied === undefined) this.stats.unreported.push(key);
      else if (applied !== config.audio.constraints[key]) {
        this.stats.ignored.push(`${key}: ${consequence}`);
      }
    }
    return track;
  }

  /**
   * Watch for the graph dying under us.
   *
   * It does. In a recorded session, Chrome's SpeechRecognition restarted
   * mid-take, took the input device, and left the AudioContext suspended. The
   * worklet stopped and the AnalyserNode kept its last buffer, so twenty-three
   * seconds of byte-identical spectral features were produced, recorded, and
   * reported as measurements. `capture.running` was true throughout.
   *
   * Two channels of this project want the one microphone, and the browser
   * arbitrates without telling anybody. So: watch the context, watch the track,
   * and — because neither is guaranteed to fire — watch whether envelope
   * batches are still arriving at all.
   */
  _watch(track) {
    this._ctx.onstatechange = () => {
      if (!this._ctx) return;
      this.stats.contextState = this._ctx.state;
      if (this._ctx.state === 'running') {
        this._set('running', 'resumed');
        return;
      }
      // 'suspended' or 'interrupted': something else took the device.
      this.stats.stalled = true;
      this._set('stalled', `audio context ${this._ctx.state}`);
      this._recover();
    };

    track?.addEventListener?.('ended', () => {
      // The track is gone for good — resuming the context recovers nothing.
      this.stats.stalled = true;
      this._set('stalled', 'input track ended');
      this._recover({ reopen: true });
    });
    track?.addEventListener?.('mute', () => this._set('running', 'input muted'));
    track?.addEventListener?.('unmute', () => this._set('running', 'input live'));
  }

  /**
   * Try to get the graph running again. Resuming is usually enough; a track
   * that has ended needs the whole thing rebuilt.
   */
  async _recover({ reopen = false } = {}) {
    if (this._recovering || !this._ctx) return;
    this._recovering = true;
    this.stats.recoveries++;
    try {
      if (reopen) {
        this.stop();
        await this.start();
      } else {
        await this._ctx.resume();
        this._lastBatchAt = performance.now();
      }
    } catch (err) {
      // Stalled, not errored. A failed resume is not fatal — the device may
      // come back — and marking it fatal would take `active` false, disarm the
      // recorder mid-take and hide the microphone behind a "start" button while
      // the user is still talking into it.
      this._set('stalled', `recovery failed: ${err?.message ?? err}`);
    } finally {
      this._recovering = false;
    }
  }

  /**
   * Has the graph stopped being fed?
   *
   * The context's own state is definitive when it is not 'running'. The batch
   * watchdog is the backstop for the case where it claims to be running and
   * is not — which is the case that actually happened.
   */
  _stalled(now) {
    if (this._ctx && this._ctx.state !== 'running') return true;
    return now - this._lastBatchAt > config.audio.STALL_MS;
  }

  /** One batch of block RMS values off the audio thread. */
  _onBatch({ frame, blockSize, rms }) {
    // A batch can outlive the graph: the worklet keeps running until the
    // context closes, and the timestamps it carries mean nothing without the
    // clock that was torn down with it.
    if (!this._clock) return;

    const arrivedAt = performance.now();
    // The liveness signal. `frame()` is the only thing that reads it, so the
    // stall decision lives in one place.
    this._lastBatchAt = arrivedAt;

    this._clock.observe(frame, arrivedAt);
    this.stats.clockOffset = this._clock.offset;

    if (this.stats.blockSize !== blockSize) {
      this.stats.blockSize = blockSize;
      this.stats.envelopeRate = this.stats.sampleRate / blockSize;
      // The rate is a runtime fact, so every window sized in seconds is sized
      // here rather than assumed at 375 Hz. A hardcoded rate turns Phase 5's
      // modRate into a plausible-looking lie on any device that is not 48 kHz.
      this.tracker.setRate(this.stats.envelopeRate);
      this.noiseFloor.setRate(this.stats.envelopeRate);
      this.envelope.setRate(this.stats.envelopeRate);
    }
    this.stats.batches++;

    for (let i = 0; i < rms.length; i++) {
      // Centre of the block. The RMS describes the whole 2.7 ms, and stamping
      // it at either edge biases every downstream measurement by half a block
      // in a direction nobody would think to look for.
      const t = this._clock.toPerformanceTime(frame + (i + 0.5) * blockSize);
      if (t === null) continue;
      const sample = { t, rms: rms[i] };
      this.buffer.push(sample);
      // The floor first: everything after it reads the gate, and reading a
      // gate computed from an older sample is a frame of lag in a quantity
      // that moves slowly anyway — but the ordering should still be the
      // obvious one.
      this.noiseFloor.push(toDb(sample.rms));
      this.tracker.push(sample);
      this.envelope.push(sample);
      this.onEnvelope(sample);
    }
    this.stats.noiseFloorDb = this.noiseFloor.floor;
    this.stats.gateDb = this.noiseFloor.gate;
  }

  /**
   * The rAF-rate feature frame, or null.
   *
   * Null covers three different things — no microphone, a torn-down graph, and
   * a graph that has stopped being fed — and the caller does not need to tell
   * them apart. What matters is the rule: **never return a frame computed from
   * a buffer that is not being refilled.** An AnalyserNode whose input has gone
   * away does not report an error or return zeros. It hands back its last
   * buffer, indefinitely, and every feature derived from it is a real number
   * describing a moment that has passed.
   *
   * @param {number} now performance.now()
   */
  frame(now) {
    if (!this.active || !this.analyser) return null;

    if (this._stalled(now)) {
      if (!this.stats.stalled) {
        this.stats.stalled = true;
        this._set('stalled', `no audio for ${Math.round(now - this._lastBatchAt)} ms`);
        this._recover();
      }
      return null;
    }

    if (this.stats.stalled) {
      this.stats.stalled = false;
      this._set('running', `recovered after ${this.stats.recoveries} attempt(s)`);
    }
    return this.analyser.frame(now);
  }

  /** Release the microphone and tear the graph down. Safe from any state. */
  stop() {
    // Detach the watcher first. Closing a context fires a statechange, and a
    // recovery kicked off by our own teardown would reopen the microphone
    // immediately after the user asked for it to be released.
    if (this._ctx) this._ctx.onstatechange = null;
    this.stats.stalled = false;
    this.stats.contextState = null;

    if (this._node) {
      this._node.port.onmessage = null;
      this._node.disconnect();
      this._node = null;
    }
    this._analyserNode?.disconnect();
    this._analyserNode = null;
    this.analyser = null;

    for (const track of this._stream?.getTracks() ?? []) track.stop();
    this._stream = null;

    // A trill still going when the microphone is released would otherwise never
    // be emitted: its duration and cycle count are not known until it ends, and
    // stopping is not ending.
    this.envelope.flush();

    this._ctx?.close?.();
    this._ctx = null;
    this._clock = null;
    this.tracker.reset();
    this.noiseFloor.reset();

    if (this.state !== 'error') this._set('idle');
  }
}

/**
 * Whether this browser can run Channel B at all. Checked before offering the
 * button, so a missing capability reads as one rather than as a microphone that
 * will not open.
 */
export function audioSupported() {
  return Boolean(
    navigator.mediaDevices?.getUserMedia && window.isSecureContext && window.AudioWorkletNode,
  );
}

export default AudioCapture;
